// The page at mchatai.com/hub/<slug> (Phase HUB-PAGE, 2026-09-27).
//
// One URL per product, and it opens the product. A web app runs at once under
// a single bar: ← Hub, its name, ⓘ, Restart, Fullscreen, and a 🏆 when the app
// has a leaderboard. ⓘ opens /hub/<slug>/info, a sheet over the running app
// with the product's description, ratings and build record (HubProductInfo.js).
// Anything that doesn't run in a browser (a walk, a prompt, media, a Mac app)
// gets that description as its whole page.
//
//   /hub/<slug>             the product
//   /hub/<slug>/info        the product with its info sheet open
//   /hub/<slug>/play        redirects to /hub/<slug> (the iPhone app's Share button sends these)
//   ?embed=ios|android      the app alone: the native app draws its own chrome
//
// /hub/<slug> and /hub/<slug>/info are one route, so opening the sheet doesn't
// reload the running app. Before 2026-09-27 this file was
// EndProductPlayerPage.js, the page at /hub/<slug>/play; `git log --follow`
// carries that history.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { fetchEndProduct } from './communityApi';
import './App.css';
import { trackEvent, scoreBucket, durationBucket } from './analytics';
import SignInModal from './SignInModal';
import useAuth from './useAuth';
import { ProductInfo } from './HubProductInfo';

// Events an embedded app is allowed to surface to GA via postMessage.
// Whitelisted so a published (user-authored) bundle can't inject arbitrary
// analytics events. See ANALYTICS_EVENT_SPEC.md (hub_app_completed).
const ALLOWED_GAME_EVENTS = new Set(['hub_app_completed']);

// A product runs in the page when it is a web app with a hosted copy.
export function webAppURL(product) {
  const category = String(product?.contentCategory || product?.type || '').toLowerCase();
  if (category !== 'miniapp' && category !== 'game') return '';
  return product.previewURL || '';
}

// The hosted copy loads with ?embed=hub. That tells the platform widget inside
// it that this page's bar already has Hub, Sign in, Feedback and the
// leaderboard, so it doesn't draw them a second time in the app's corner. A
// native player (?embed=ios|android) passes its own value through instead.
//
// Hosted copies live under /u/ on mchatai.com and refuse to be framed from
// another origin (X-Frame-Options: SAMEORIGIN). This page is served by the
// same hosting, which also serves /u/, so the copy loads from this page's own
// origin: mchatai.com in production, the channel's domain in a preview.
export function hubFrameURL(url, embed = 'hub') {
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('embed', embed);
    if (parsed.hostname === 'mchatai.com' && parsed.pathname.startsWith('/u/')) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    return parsed.toString();
  } catch (_) {
    return url;
  }
}

// Paths this page owns, so the site's TopNav stays out of the way.
// /hub/macosapps is a separate listing page, not a product.
export function isHubProductPath(pathname) {
  return /^\/hub\/(?!macosapps(?:\/|$))[^/]+(?:\/[^/]*)?\/?$/.test(pathname || '');
}

// /community-hub/<slug> was the page's first address.
export function LegacyHubRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/hub/${encodeURIComponent(slug || '')}`} replace />;
}

function Icon({ children, ...props }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      {children}
    </svg>
  );
}
const InfoIcon = () => <Icon><circle cx="12" cy="12" r="9" /><path d="M12 11v5" /><path d="M12 7.5h.01" /></Icon>;
const RestartIcon = () => <Icon><path d="M4 12a8 8 0 1 0 2.4-5.7" /><path d="M4 4v4.5h4.5" /></Icon>;
const FullscreenIcon = () => <Icon><path d="M4 9V4h5" /><path d="M20 9V4h-5" /><path d="M4 15v5h5" /><path d="M20 15v5h-5" /></Icon>;
const TrophyIcon = () => <Icon><path d="M8 4h8v5a4 4 0 0 1-8 0V4z" /><path d="M8 6H5v1a3 3 0 0 0 3 3" /><path d="M16 6h3v1a3 3 0 0 1-3 3" /><path d="M12 13v4" /><path d="M9 20h6" /></Icon>;
const BackIcon = () => <Icon><path d="M15 5l-7 7 7 7" /></Icon>;

function canFullscreen() {
  return !!(document.fullscreenEnabled || document.webkitFullscreenEnabled);
}

function ProductBar({ product, app, onInfo, onRestart, onFullscreen, leaderboardURL, user, onSignIn }) {
  const author = product.publisherName || product.publisher?.displayName || '';
  const isRealUser = !!(user && !user.isAnonymous);
  const initial = (user?.displayName || user?.email || '?').trim().charAt(0).toUpperCase();
  return (
    <header className="hp-bar">
      <Link to="/" className="hp-bar-home">mChatAI</Link>
      <Link to="/hub" className="hp-icon-btn" title="Back to the Hub" aria-label="Back to the Hub"><BackIcon /></Link>
      {product.iconURL && <img className="hp-bar-icon" src={product.iconURL} alt="" />}
      <div className="hp-bar-title">
        <span className="hp-bar-name">{product.name}</span>
        {author && <span className="hp-bar-author"> · {author}</span>}
      </div>
      <div className="hp-bar-actions">
        {leaderboardURL && (
          <Link to={leaderboardURL} className="hp-icon-btn" title="Leaderboard" aria-label="Leaderboard"><TrophyIcon /></Link>
        )}
        {app && (
          <button type="button" className="hp-icon-btn" onClick={onInfo} title="About this app" aria-label="About this app"><InfoIcon /></button>
        )}
        {app && (
          <button type="button" className="hp-icon-btn" onClick={onRestart} title="Restart" aria-label="Restart"><RestartIcon /></button>
        )}
        {app && canFullscreen() && (
          <button type="button" className="hp-icon-btn hp-bar-fs" onClick={onFullscreen} title="Fullscreen (Esc to leave)" aria-label="Fullscreen"><FullscreenIcon /></button>
        )}
        {isRealUser
          ? <Link to="/account" className="hp-bar-account" title={user.displayName || user.email || 'Account'}>{initial}</Link>
          : <button type="button" className="hp-bar-signin" onClick={onSignIn}>Sign in</button>}
      </div>
    </header>
  );
}

function InfoSheet({ onClose, children }) {
  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="hp-sheet-backdrop" onClick={onClose}>
      <aside className="hp-sheet" role="dialog" aria-modal="true" aria-label="About" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="hp-sheet-close" onClick={onClose} aria-label="Close">×</button>
        {children}
      </aside>
    </div>
  );
}

export default function HubProductPage() {
  const { slug, section } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const embed = searchParams.get('embed') || '';
  const embedded = !!embed;
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [leaderboardURL, setLeaderboardURL] = useState('');
  const stageRef = useRef(null);
  const frameRef = useRef(null);
  const probeRef = useRef(null);
  // Is the visitor already signed in on mchatai.com (cached creds)? If so a
  // game's sign-in request needs no prompt. A ref mirror lets the stable
  // listeners read the latest value without re-subscribing.
  const { user } = useAuth();
  const isRealUser = !!(user && !user.isAnonymous);
  const isRealUserRef = useRef(isRealUser);
  isRealUserRef.current = isRealUser;

  useEffect(() => {
    let live = true;
    setProduct(null);
    setNotFound(false);
    setLeaderboardURL('');
    fetchEndProduct(slug)
      .then((found) => { if (!live) return; if (found) setProduct(found); else setNotFound(true); })
      .catch(() => { if (live) setNotFound(true); });
    return () => { live = false; };
  }, [slug]);

  const appURL = product ? webAppURL(product) : '';

  // The tab says what's open. The prerendered page already did, for the first load.
  useEffect(() => {
    if (!product) return undefined;
    const previous = document.title;
    document.title = `${product.name} — mChatAI`;
    return () => { document.title = previous; };
  }, [product]);

  // hub_play fires once when a web app is on screen. The name predates this
  // page and stays, so the analytics series continues.
  useEffect(() => {
    if (!product || !appURL) return;
    trackEvent('hub_play', {
      product_id: product.id || product._id || slug,
      category: product.contentCategory || product.type || '',
      is_installed: false,
    });
  }, [product, appURL, slug]);

  // Bridge in-app completion/score to GA. The app posts
  // {type:'mchatai_event', name:'hub_app_completed', score, durationMs, result}
  // to its parent; we sanitize and re-emit. Never trust bundle-supplied strings
  // beyond the whitelisted name; bucket numbers so nothing high-cardinality
  // (or fingerprint-y) reaches GA.
  useEffect(() => {
    function onMessage(event) {
      const data = event && event.data;
      if (!data || data.type !== 'mchatai_event') return;
      if (!ALLOWED_GAME_EVENTS.has(data.name)) return;
      trackEvent(data.name, {
        slug: slug || '',
        result: typeof data.result === 'string' ? data.result.slice(0, 40) : '',
        score_bucket: scoreBucket(data.score),
        duration_bucket: durationBucket(data.durationMs),
      });
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [slug]);

  // Tell the app its auth may have changed, so the leaderboard bridge re-checks
  // the shared session and re-submits a pending score (no-op if still signed out).
  const postAuthChanged = useCallback(() => {
    try {
      const win = frameRef.current && frameRef.current.contentWindow;
      // The frame is same-origin with this page (see hubFrameURL).
      if (win) win.postMessage({ type: 'mchatai:auth-changed' }, window.location.origin);
    } catch (_) { /* best-effort */ }
  }, []);

  // In-place sign-in handshake with the app's leaderboard bridge. The bridge
  // posts 'mchatai:request-signin'; we ack (so it cancels its /account
  // fallback). A visitor already signed in skips the box; otherwise it opens here.
  useEffect(() => {
    function onSignInRequest(event) {
      if (event.origin !== 'https://mchatai.com' && event.origin !== window.location.origin) return;
      if (!event.data || event.data.type !== 'mchatai:request-signin') return;
      try {
        if (event.source && event.source.postMessage) event.source.postMessage({ type: 'mchatai:signin-ack' }, event.origin);
      } catch (_) { /* ack is best-effort */ }
      if (isRealUserRef.current) postAuthChanged();
      else setShowSignIn(true);
    }
    window.addEventListener('message', onSignInRequest);
    return () => window.removeEventListener('message', onSignInRequest);
  }, [postAuthChanged]);

  // The hosted copy is same-origin, so after it loads the page can see whether
  // its leaderboard bridge switched on, and offer the 🏆 in the bar.
  const onFrameLoad = useCallback(() => {
    if (isRealUserRef.current) postAuthChanged();
    clearInterval(probeRef.current);
    let tries = 0;
    probeRef.current = setInterval(() => {
      tries += 1;
      try {
        const win = frameRef.current && frameRef.current.contentWindow;
        if (win && win.__MCHATAI_HAS_LEADERBOARD__) {
          setLeaderboardURL(win.__MCHATAI_LEADERBOARD_URL__ || '');
          clearInterval(probeRef.current);
        }
      } catch (_) {
        clearInterval(probeRef.current); // cross-origin: can't see in, so no 🏆
      }
      if (tries >= 20) clearInterval(probeRef.current);
    }, 250);
  }, [postAuthChanged]);
  useEffect(() => () => clearInterval(probeRef.current), []);

  const openInfo = useCallback(() => {
    navigate(`/hub/${encodeURIComponent(slug)}/info${location.search}`, { state: { fromBar: true } });
  }, [navigate, slug, location.search]);

  // Back out of the sheet the way it was opened: with history if ⓘ pushed
  // it, by replacing the URL if the visitor arrived straight on /info.
  const closeInfo = useCallback(() => {
    if (location.state && location.state.fromBar) navigate(-1);
    else navigate(`/hub/${encodeURIComponent(slug)}${location.search}`, { replace: true });
  }, [navigate, slug, location.state, location.search]);

  const toggleFullscreen = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const active = document.fullscreenElement || document.webkitFullscreenElement;
    if (active) {
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      if (exit) exit.call(document);
      return;
    }
    const request = stage.requestFullscreen || stage.webkitRequestFullscreen;
    if (request) {
      const result = request.call(stage);
      if (result && typeof result.catch === 'function') result.catch(() => {});
    }
  }, []);

  const handleSignInClose = () => {
    setShowSignIn(false);
    postAuthChanged();
  };

  if (section && section !== 'info') {
    return <Navigate to={`/hub/${encodeURIComponent(slug)}${location.search}`} replace />;
  }
  if (notFound) {
    return (
      <div className="hp-root hp-root--page">
        <main className="hp-page hp-page--empty">
          <h1>Nothing at this address</h1>
          <p>There's no product called "{slug}" on the Hub. It may have been unpublished.</p>
          <p><Link to="/hub">Browse the Hub</Link></p>
        </main>
      </div>
    );
  }
  if (!product) {
    return <div className="hp-root hp-root--page"><p className="hp-loading">Loading…</p></div>;
  }

  const bar = (app) => (
    <ProductBar
      product={product}
      app={app}
      onInfo={openInfo}
      onRestart={() => setReloadNonce((n) => n + 1)}
      onFullscreen={toggleFullscreen}
      leaderboardURL={leaderboardURL}
      user={user}
      onSignIn={() => setShowSignIn(true)}
    />
  );

  // A walk, a prompt, media or a Mac app: the description is the page.
  if (!appURL) {
    if (section === 'info') return <Navigate to={`/hub/${encodeURIComponent(slug)}${location.search}`} replace />;
    return (
      <div className="hp-root hp-root--page">
        {!embedded && bar(false)}
        <main className="hp-page">
          <ProductInfo product={product} slug={slug} variant="page" onProductChange={setProduct} />
        </main>
        {showSignIn && <SignInModal onClose={handleSignInClose} />}
      </div>
    );
  }

  const frame = (
    <iframe
      key={reloadNonce}
      ref={frameRef}
      onLoad={onFrameLoad}
      src={hubFrameURL(appURL, embedded ? embed : 'hub')}
      title={product.name}
      className="hp-frame"
      // 3D games need pointer-lock + fullscreen, and mobile games need motion
      // sensors / gamepad. Chrome blocks these inside a sandboxed iframe unless
      // explicitly granted, so a "click to play" that calls requestPointerLock
      // silently fails. Grant them here.
      sandbox="allow-scripts allow-same-origin allow-pointer-lock allow-orientation-lock allow-modals allow-popups allow-top-navigation-by-user-activation"
      allow="fullscreen; pointer-lock; gamepad; accelerometer; gyroscope; autoplay; xr-spatial-tracking"
      allowFullScreen
    />
  );

  if (embedded) {
    return <div className="hp-root hp-root--bare"><div className="hp-stage" ref={stageRef}>{frame}</div></div>;
  }

  return (
    <div className="hp-root">
      {bar(true)}
      <div className="hp-stage" ref={stageRef}>{frame}</div>
      {section === 'info' && (
        <InfoSheet onClose={closeInfo}>
          <ProductInfo product={product} slug={slug} variant="sheet" onProductChange={setProduct} />
        </InfoSheet>
      )}
      {showSignIn && <SignInModal onClose={handleSignInClose} />}
    </div>
  );
}
